import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { StopCircle, TriangleAlert } from 'lucide-react';
import { api, parseApiError, type Competition, type Me, type Role } from '../lib/api';
import { canWrite } from '../lib/roles';
import { hasOrgManagerMembership } from '../lib/orgAccess';

// ─────────────────────────────────────────────────────────────────────────────
// EndCompetitionButton — the org-manager-reachable counterpart to
// ActivateCompetitionButton for POST /competitions/end. Same legacy
// comp-lifecycle route AdminPanel.tsx's founder-tier CompRow "End" button has
// always called — no new endpoint, no new gate. Extracted as its OWN
// component (not folded into ActivateCompetitionButton) because Activate and
// End are mutually exclusive by status (PENDING vs LIVE) and each carries its
// own confirm copy — a shared component branching on both would read muddier
// than two small ones mounted side by side.
//
// Gating mirrors ActivateCompetitionButton EXACTLY: founder/admin OR this
// comp's own org's ORG_ADMIN/ORG_OWNER — widened 2026-07-24 (single role-spine
// rewire, ADMIN can now run comps); retires the 2026-07-17 OWNER-ONLY
// carve-out (canActivateCompetitionOrgOwner), which gated END the same as
// activate/advance-stage/create-divisions. `orgId`/`me` optional so any
// caller that passes only `role` keeps founder/admin-only behaviour unchanged.
//
// The "are you sure" step is a custom inline confirm (house idiom — see
// ActivateCompetitionButton's own header comment for why not window.confirm).
// No colour/severity strip — the warning is carried by icon + typography.
// ─────────────────────────────────────────────────────────────────────────────

export function EndCompetitionButton({
  compId,
  status,
  role,
  orgId,
  me,
  onEnded,
  className,
}: {
  compId: string;
  status: Competition['status'];
  role?: Role | null;
  /** The comp's OWN parent org (Competition.orgId) — for the org-owner leg. */
  orgId?: string | null;
  /** effectiveMe — for the org-owner membership check (view-as-aware). */
  me?: Me | null;
  onEnded?: () => void;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => api.endCompetition(compId),
    onSuccess: () => {
      setError('');
      setConfirming(false);
      onEnded?.();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Founder/admin, or this comp's own org's ORG_ADMIN/ORG_OWNER — ORG_MEMBER
  // and below never see this control at all, regardless of what else they
  // can view on the surrounding page.
  if (!canWrite(role) && !hasOrgManagerMembership(me, orgId)) return null;
  // Nothing to end unless it's currently LIVE.
  if (status !== 'LIVE') return null;

  if (confirming) {
    return (
      <div className={className}>
        <div className="rounded-lg bg-white/[0.04] p-3.5">
          <p className="flex items-center gap-1.5 font-display text-[11px] font-black uppercase tracking-wider text-ink-secondary">
            <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.25} /> Are you sure?
          </p>
          <p className="mt-2 text-xs leading-relaxed text-ink-body">
            Ending closes this competition <strong className="font-semibold text-ink-primary">permanently</strong> —
            no further matches, results, or edits. A final report is generated automatically.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              className="btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
            >
              <StopCircle className="h-3.5 w-3.5" /> {mut.isPending ? 'Ending…' : 'Yes, end it'}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="btn-ghost px-3 py-1.5 text-xs">
              Cancel
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={mut.isPending}
        className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
      >
        <StopCircle className="h-3.5 w-3.5" />
        End
      </button>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
